import type {
  DeferredChargeSettingsDto,
  DeferredMembershipPreviewDto,
  PreviewDeferredMembershipDto,
  DeferredMembershipPurchaseDto,
  DeferredBookingAccessDto,
  PrepareDeferredBookingDto,
} from "@zipper/embedded-api-client";
import {
  getEmbeddedApiClients,
  type EmbeddedApiClients,
} from "../data/embedded-api";
import type { OrderTotals } from "../checkout/order-totals";

export interface DeferredCheckoutTarget {
  orgId: number;
  rosterId: number;
  purchasedForRosterId?: number | undefined;
  checkoutToken?: string | undefined;
  stripeProductId: string;
  settings: DeferredChargeSettingsDto;
  bookingCheckout: boolean;
  api?: EmbeddedApiClients | undefined;
}

function body(target: DeferredCheckoutTarget): PreviewDeferredMembershipDto {
  if (!target.settings.enabled || !target.settings.triggerType) {
    throw new Error(
      "This membership's payment timing is unavailable. Please refresh.",
    );
  }
  if (
    target.checkoutToken &&
    target.purchasedForRosterId &&
    target.purchasedForRosterId !== target.rosterId
  ) {
    throw new Error(
      "Please sign in to reserve a membership for a family member.",
    );
  }
  return {
    rosterId: target.rosterId,
    ...(target.purchasedForRosterId === undefined
      ? {}
      : { purchasedForRosterId: target.purchasedForRosterId }),
    ...(target.checkoutToken === undefined
      ? {}
      : { checkoutToken: target.checkoutToken }),
    stripeProductId: target.stripeProductId,
    triggerType: target.settings.triggerType,
    ...(target.settings.fixedChargeDate
      ? { fixedChargeDate: target.settings.fixedChargeDate }
      : {}),
    bookingCheckout: target.bookingCheckout,
  };
}

export function deferredQuoteTotals(
  quote: DeferredMembershipPreviewDto,
  chargeOnBooking: boolean,
): OrderTotals {
  if (
    !quote.valid ||
    !Number.isSafeInteger(quote.futureChargeAmountInCents) ||
    quote.futureChargeAmountInCents < 0 ||
    !quote.consentText ||
    !quote.consentTextVersion ||
    !/^[a-f0-9]{64}$/.test(quote.snapshotHash)
  ) {
    throw new Error(
      "We couldn't confirm this membership's payment terms. Please refresh and try again.",
    );
  }
  return {
    quoted: true,
    subtotalInCents: chargeOnBooking
      ? quote.recurringAmountInCents +
        (quote.startupFeeAmountInCents ?? quote.startupFeeInCents ?? 0)
      : 0,
    discountInCents: 0,
    platformFeeInCents: chargeOnBooking
      ? (quote.estimatedFeeAmountInCents ?? 0)
      : 0,
    taxInCents: chargeOnBooking ? (quote.estimatedTaxAmountInCents ?? 0) : 0,
    totalInCents: chargeOnBooking ? quote.futureChargeAmountInCents : 0,
    recurringTotalInCents: quote.recurringAmountInCents,
  };
}

export async function previewDeferredCheckout(
  target: DeferredCheckoutTarget,
): Promise<DeferredMembershipPreviewDto> {
  const api = (target.api ?? getEmbeddedApiClients(target.orgId))
    .deferredMemberships;
  const params = {
    orgId: target.orgId,
    previewDeferredMembershipDto: body(target),
  };
  return target.checkoutToken
    ? api.previewDeferredMembershipGuest(params)
    : api.previewDeferredMembership(params);
}

export async function createDeferredCheckoutSetup(
  target: DeferredCheckoutTarget,
  quote: DeferredMembershipPreviewDto,
  chargeAttemptId: string,
) {
  const api = (target.api ?? getEmbeddedApiClients(target.orgId))
    .deferredMemberships;
  const params = {
    orgId: target.orgId,
    createDeferredSetupIntentDto: {
      ...body(target),
      paymentMethodType: "card",
      consentTextVersion: quote.consentTextVersion,
      chargeAttemptId,
    },
  };
  const setup = target.checkoutToken
    ? await api.createSetupIntentGuest(params)
    : await api.createSetupIntent(params);
  if (
    !setup.clientSecret ||
    setup.consentText !== quote.consentText ||
    setup.consentTextVersion !== quote.consentTextVersion
  ) {
    throw new Error(
      "The membership terms have changed. Please refresh and review them again.",
    );
  }
  return setup;
}

export async function reserveDeferredCheckout(
  target: DeferredCheckoutTarget,
  quote: DeferredMembershipPreviewDto,
  setup: { setupIntentId: string; paymentMethodId: string },
  chargeAttemptId: string,
): Promise<DeferredMembershipPurchaseDto> {
  const api = (target.api ?? getEmbeddedApiClients(target.orgId))
    .deferredMemberships;
  const params = {
    orgId: target.orgId,
    createDeferredMembershipPurchaseDto: {
      ...body(target),
      ...setup,
      chargeAttemptId,
      acceptedConsentText: quote.consentText,
      acceptedConsentTextVersion: quote.consentTextVersion,
      snapshotHash: quote.snapshotHash,
    },
  };
  return target.checkoutToken
    ? api.createDeferredMembershipPurchaseGuest(params)
    : api.createDeferredMembershipPurchase(params);
}

export function deferredCheckoutError(error: unknown): string {
  const response = error as { status?: number; body?: { message?: string | string[] } };
  const message = typeof response?.body?.message === "string"
    ? response.body.message
    : Array.isArray(response?.body?.message)
      ? response.body.message.join(" ")
      : error instanceof Error ? error.message : "";
  if ((response?.status ?? 0) >= 500 || /interceptor|failed to fetch|fetch failed|networkerror|network request|load failed|econn|aborterror/i.test(message)) {
    return "We couldn’t complete the request. Please try again.";
  }
  return message || "Could not reserve this membership. Please try again.";
}

export function deferredTermsNeedReview(error: unknown): boolean {
  const message = deferredCheckoutError(error);
  return /(?:terms|consent|quote|snapshot).*(?:changed|no longer matches)|no longer matches.*(?:terms|consent|quote|snapshot)|review.*payment terms again/i.test(message)
    || /deferred charge date no longer matches the product configuration|deferred charging is not enabled for this product and trigger/i.test(message);
}

export async function prepareDeferredBooking(orgId: number, request: PrepareDeferredBookingDto, api?: EmbeddedApiClients): Promise<DeferredBookingAccessDto> {
  const client = (api ?? getEmbeddedApiClients(orgId)).deferredMemberships;
  const params = { orgId, prepareDeferredBookingDto: request };
  return request.checkoutToken ? client.prepareDeferredBookingGuest(params) : client.prepareDeferredBooking(params);
}
